import { useState, useEffect } from 'react';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { usePermissions } from '@/common/hooks/usePermissions';
import {
  documentsService,
  Document,
  CreateDocumentDto,
  UpdateDocumentDto,
} from '@/features/documents/services/documents.service';

export const Route = createFileRoute('/_authenticated/documents-management')({
  component: DocumentsManagementPage,
});

const documentSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  downloadUrl: z.string().url('URL inválida').min(1, 'Link é obrigatório'),
  fileSize: z.string().optional(),
});

type DocumentFormData = z.infer<typeof documentSchema>;

function DocumentsManagementPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const confirm = useConfirm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canManage = hasPermission('documents.manage');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DocumentFormData>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      name: '',
      description: '',
      downloadUrl: '',
      fileSize: '',
    },
  });

  // Buscar documentos
  const {
    data: documents,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      try {
        const response = await documentsService.getAll();
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Erro ao buscar documentos:', error);
        return [];
      }
    },
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => documentsService.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      await refetch();
    },
  });

  useEffect(() => {
    if (selectedDocument) {
      reset({
        name: selectedDocument.name,
        description: selectedDocument.description || '',
        downloadUrl: selectedDocument.downloadUrl,
        fileSize: selectedDocument.fileSize || '',
      });
    } else {
      reset({
        name: '',
        description: '',
        downloadUrl: '',
        fileSize: '',
      });
    }
  }, [selectedDocument, reset, formOpen]);

  const handleDelete = async (document: Document) => {
    const ok = await confirm({
      title: `Excluir o documento "${document.name}"?`,
      description: 'Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir documento',
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(document.id);
    } catch (error) {
      // o interceptor do axios já mostra o toast de erro
      console.error('Erro ao excluir:', error);
    }
  };

  const handleEdit = (document: Document) => {
    setSelectedDocument(document);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedDocument(null);
    setFormOpen(true);
  };

  const onSubmit = async (data: DocumentFormData) => {
    setIsSubmitting(true);
    try {
      if (selectedDocument) {
        const payload: UpdateDocumentDto = {
          name: data.name,
          description: data.description || undefined,
          downloadUrl: data.downloadUrl,
          fileSize: data.fileSize || undefined,
        };
        await documentsService.update(selectedDocument.id, payload);
      } else {
        const payload: CreateDocumentDto = {
          name: data.name,
          description: data.description || undefined,
          downloadUrl: data.downloadUrl,
          fileSize: data.fileSize || undefined,
        };
        await documentsService.create(payload);
      }

      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      await refetch();
      setFormOpen(false);
    } catch (error) {
      // o interceptor do axios já mostra o toast de erro
      console.error('Erro ao salvar documento:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Acesso Negado</h1>
          <p className="text-muted-foreground mt-2">
            Você não tem permissão para acessar esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestão de Documentos</h1>
          <p className="text-muted-foreground">Gerencie os documentos disponíveis para os usuários</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Documento
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Documentos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : !documents || documents.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nenhum documento cadastrado. Clique em "Adicionar Documento" para começar.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Tamanho</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((document) => (
                    <TableRow key={document.id}>
                      <TableCell className="font-medium">{document.name}</TableCell>
                      <TableCell>
                        {document.description || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <a
                          href={document.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline truncate max-w-xs block"
                        >
                          {document.downloadUrl}
                        </a>
                      </TableCell>
                      <TableCell>
                        {document.fileSize || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(document)}
                            title="Editar"
                            aria-label="Editar documento"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(document)}
                            title="Excluir"
                            aria-label="Excluir documento"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Formulário */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent size="md" busy={isSubmitting}>
          <DialogHeader>
            <DialogTitle>
              {selectedDocument ? 'Editar Documento' : 'Novo Documento'}
            </DialogTitle>
            <DialogDescription>
              {selectedDocument
                ? 'Altere as informações do documento abaixo.'
                : 'Preencha os dados para criar um novo documento.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Nome <span className="text-destructive">*</span>
              </Label>
              <Input id="name" {...register('name')} />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea id="description" {...register('description')} rows={3} />
            </div>

            <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
            <div className="space-y-2">
              <Label htmlFor="downloadUrl">
                Link/URL <span className="text-destructive">*</span>
              </Label>
              <Input id="downloadUrl" type="url" {...register('downloadUrl')} />
              {errors.downloadUrl && (
                <p className="text-sm text-destructive">{errors.downloadUrl.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="fileSize">Tamanho</Label>
              <Input id="fileSize" {...register('fileSize')} placeholder="2.5 MB" />
            </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormOpen(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={isSubmitting}>
                {selectedDocument ? 'Salvar alterações' : 'Criar documento'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


